import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, CalendarDays, Inbox, Users,
  Shield, Settings, LogOut, Menu, X, Sun, Moon, Plug
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useTheme } from '../../hooks/useTheme';
import { Avatar } from '../ui/Avatar';
import apiService from '../../services/api';
import toast from 'react-hot-toast';

const NAV_ALL = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true  },
  { to: '/requests',  label: 'Requests',  icon: Inbox,           end: false },
  { to: '/calendar',  label: 'Calendar',  icon: CalendarDays,    end: false },
  { to: '/team',      label: 'Team',      icon: Users,           end: false },
  { to: '/integrations', label: 'Integrations', icon: Plug,      end: false },
  { to: '/settings',  label: 'Settings',  icon: Settings,        end: false },
];

// Everyone can sync their own leave to their calendar, so members see Integrations too
const MEMBER_ROUTES = ['/dashboard', '/calendar', '/integrations'];

// Kept out of the phone's bottom bar so it doesn't get crowded; still in the menu
const MENU_ONLY = ['/integrations'];

function useWindowWidth() {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return width;
}

export function Sidebar({ open, onClose }) {
  const { currentUser, pendingCount, loadingUser, isDepartmentAdmin } = useApp();
  const { theme, setTheme } = useTheme();
  const navigate    = useNavigate();
  const location    = useLocation();
  const windowWidth = useWindowWidth();
  const [loggingOut, setLoggingOut] = useState(false);

  const isDesktop = windowWidth >= 1024;
  const isMobile  = windowWidth < 640;

  const role = currentUser?.role;
  const NAV = loadingUser
    ? []
    : role === 'member'
      // Members who are department admins also get Requests, to approve their team's leave
      ? NAV_ALL.filter(n => MEMBER_ROUTES.includes(n.to) || (n.to === '/requests' && isDepartmentAdmin))
      : NAV_ALL;

  const BOTTOM_NAV = NAV.map(n => n.to).filter(to => !MENU_ONLY.includes(to));

  useEffect(() => { if (!isDesktop) onClose?.(); }, [location.pathname]);

  useEffect(() => {
    if (!isDesktop && open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open, isDesktop]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await apiService.logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch {
      navigate('/login');
    } finally {
      setLoggingOut(false);
    }
  };

  const sidebarContent = (
    <aside
      style={{
        width: 220,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--side-bg)',
        borderRight: '1px solid var(--side-border)',
        transition: 'background-color 0.2s',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* Header & Logo */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 10,
          padding: '22px 18px 18px',
          borderBottom: '1px solid var(--side-divider)',
          position: 'relative',
        }}
      >
        {!isDesktop && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 12, right: 12,
              background: 'var(--side-chip)', border: 'none',
              borderRadius: 8, width: 30, height: 30,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'var(--side-text-strong)',
            }}
          >
            <X size={16} />
          </button>
        )}

        <img
          className="logo-on-light"
          src="/assets/clockout1.png"
          alt="Clock-out"
          style={{ height: 44, width: '100%', objectFit: 'contain', objectPosition: 'left center' }}
        />
        <img
          className="logo-on-dark"
          src="/assets/clockout2.png"
          alt="Clock-out"
          style={{ height: 44, width: '100%', objectFit: 'contain', objectPosition: 'left center' }}
        />
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' }}>
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            style={({ isActive }) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 12px',
              borderRadius: 12,
              textDecoration: 'none',
              transition: 'all 0.15s ease',
              backgroundColor: isActive ? 'var(--side-active-bg)' : 'transparent',
              color: isActive ? 'var(--side-active-text)' : 'var(--side-text)',
            })}
            onMouseEnter={e => {
              const active = e.currentTarget.classList.contains('active');
              if (!active) {
                e.currentTarget.style.backgroundColor = 'var(--side-hover)';
                e.currentTarget.style.color = 'var(--side-text-strong)';
              }
            }}
            onMouseLeave={e => {
              const active = e.currentTarget.classList.contains('active');
              e.currentTarget.style.backgroundColor = active ? 'var(--side-active-bg)' : 'transparent';
              e.currentTarget.style.color = active ? 'var(--side-active-text)' : 'var(--side-text)';
            }}
          >
            {({ isActive }) => (
              <>
                <Icon
                  size={18}
                  style={{ flexShrink: 0, color: isActive ? 'var(--side-active-text)' : 'var(--side-muted)' }}
                />
                <span style={{ flex: 1, fontSize: 13, fontWeight: isActive ? 600 : 500 }}>
                  {label}
                </span>
                {label === 'Requests' && pendingCount > 0 && (
                  <span style={{
                    width: 18, height: 18, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 700,
                    backgroundColor: isActive && theme === 'light' ? '#1C1A14' : '#FFC81E',
                    color: isActive && theme === 'light' ? '#FFC81E' : '#1C1A14',
                    flexShrink: 0,
                  }}>
                    {pendingCount > 9 ? '9+' : pendingCount}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer / User Info */}
      <div style={{ padding: '12px 10px', borderTop: '1px solid var(--side-divider)' }}>
        {/* Light / dark switch */}
        <div className="theme-toggle" role="group" aria-label="Colour theme">
          <button type="button" aria-pressed={theme === 'light'} onClick={() => setTheme('light')}>
            <Sun size={14} /> Light
          </button>
          <button type="button" aria-pressed={theme === 'dark'} onClick={() => setTheme('dark')}>
            <Moon size={14} /> Dark
          </button>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 10px', borderRadius: 12,
          marginBottom: 6,
          background: 'var(--side-hover)',
          border: '1px solid var(--side-divider)'
        }}>
          <Avatar employee={currentUser} size="sm" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--side-text-strong)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentUser?.name || `${currentUser?.firstName ?? ''} ${currentUser?.lastName ?? ''}`}
            </p>
            <p style={{ fontSize: 11, color: 'var(--side-muted)', margin: 0, textTransform: 'capitalize' }}>
              {currentUser?.role?.replace(/_/g, ' ')}
            </p>
          </div>
        </div>

        <button
          onClick={handleLogout}
          disabled={loggingOut}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            width: '100%', padding: '8px 10px', borderRadius: 12,
            border: 'none', background: 'transparent',
            color: 'var(--bad-text)', fontSize: 12, fontWeight: 500,
            cursor: loggingOut ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s',
            opacity: loggingOut ? 0.7 : 1,
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255, 138, 128, 0.10)'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <LogOut size={14} />
          {loggingOut ? 'Signing out…' : 'Sign Out'}
        </button>
      </div>
    </aside>
  );

  if (isDesktop) {
    return (
      <div style={{ width: 220, flexShrink: 0, height: '100vh', position: 'sticky', top: 0 }}>
        {sidebarContent}
      </div>
    );
  }

  return (
    <>
      {open && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 40,
            background: 'rgba(0,0,0,0.3)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            animation: 'fadeIn 0.15s ease',
          }}
        />
      )}

      <div
        style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          zIndex: 50, width: 220,
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: open ? '4px 0 24px rgba(0,0,0,0.1)' : 'none',
        }}
      >
        {sidebarContent}
      </div>

      {isMobile && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 45,
          height: 60,
          background: 'var(--side-bg)',
          borderTop: '1px solid var(--side-border)',
          display: 'flex', alignItems: 'stretch',
          paddingBottom: 'env(safe-area-inset-bottom)',
          boxShadow: '0 -4px 16px rgba(0,0,0,0.05)',
        }}>
          {NAV.filter(n => BOTTOM_NAV.includes(n.to)).map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              style={({ isActive }) => ({
                flex: 1,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: 3, textDecoration: 'none',
                color: isActive ? 'var(--side-text-strong)' : 'var(--side-muted)',
                position: 'relative',
                transition: 'color 0.15s',
                paddingBottom: 4,
              })}
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span style={{
                      position: 'absolute', top: 0,
                      width: 24, height: 3,
                      borderRadius: '0 0 2px 2px', background: '#FFC81E',
                    }} />
                  )}
                  <div style={{ position: 'relative' }}>
                    <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                    {label === 'Requests' && pendingCount > 0 && (
                      <span style={{
                        position: 'absolute', top: -4, right: -6,
                        minWidth: 14, height: 14, borderRadius: 7,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, fontWeight: 700,
                        background: '#FFC81E', color: '#1C1A14',
                        padding: '0 3px',
                      }}>
                        {pendingCount > 9 ? '9+' : pendingCount}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: isActive ? 600 : 500 }}>
                    {label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      )}
    </>
  );
}

export function MobileHeader({ onMenuOpen }) {
  const location    = useLocation();
  const windowWidth = useWindowWidth();
  const { pendingCount, currentUser, loadingUser, isDepartmentAdmin } = useApp();

  if (windowWidth >= 1024) return null;

  const role = currentUser?.role;
  
  const NAV = loadingUser
    ? []
    : role === 'member'
      // Members who are department admins also get Requests, to approve their team's leave
      ? NAV_ALL.filter(n => MEMBER_ROUTES.includes(n.to) || (n.to === '/requests' && isDepartmentAdmin))
      : NAV_ALL;

  const current = NAV.find(n => {
    if (n.end) return location.pathname === n.to;
    return location.pathname.startsWith(n.to);
  });
  const title = current?.label ?? 'Clock-out';

  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 35,
      height: 56,
      background: 'var(--side-bg)',
      borderBottom: '1px solid var(--side-border)',
      display: 'flex', alignItems: 'center',
      padding: '0 16px',
      gap: 12,
      paddingTop: 'env(safe-area-inset-top)',
    }}>
      <button
        onClick={onMenuOpen}
        style={{
          background: 'var(--side-chip)', border: 'none',
          borderRadius: 10, width: 34, height: 34, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: 'var(--side-text-strong)',
        }}
        aria-label="Open menu"
      >
        <Menu size={18} />
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
        <img 
          src="/assets/clockout-mark-yellow.png" 
          alt="" 
          style={{ height: 28, width: 'auto', objectFit: 'contain' }} 
        />
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--side-text-strong)' }}>{title}</span>
      </div>

      {pendingCount > 0 && (role !== 'member' || isDepartmentAdmin) && (
        <span style={{
          padding: '3px 8px', borderRadius: 20,
          fontSize: 11, fontWeight: 700,
          background: '#FFC81E', color: '#1C1A14',
        }}>
          {pendingCount} pending
        </span>
      )}
    </header>
  );
}